import gql from "graphql-tag"
import moment from 'moment'
import  { ucfirst } from "../plugins/uniweb";

/**
 *
 * @class BehaviourComponentManager
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.5] app/src/manager/BehaviourComponentManager.js
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export class BehaviourComponentManager {
  constructor(
    apolloMobileClient,
    translationManager
  ) {
    /** @type ApolloClient */
    this.apolloMobileClient = apolloMobileClient

    /** @type TranslationManager */
    this.translationManager = translationManager
  }

  /**
   *
   * @param patientId
   * @param behaviourComponents
   * @param levels
   * @param asMoment
   *
   * @return {Promise<{}>}
   *
   * @example  getPeriodsForBehaviourComponents(100, 'MEDICATIONS', 1)
   * gets the periods that patient with patientId 100 was level of guidance 1 for the MEDICATIONS behaviour component
   *
   * @example  getPeriodsForBehaviourComponents(100, 'MEDICATIONS', [1,2], true)
   * gets the periods that patient with patientId 100 was either level of guidance 1 or level of guidance 2 for the MEDICATIONS behaviour component
   * true => returns as moment objects instead of date strings
   */
  async getPeriodsForBehaviourComponents(patientId, behaviourComponents, levels, asMoment = false) {
    if (!Array.isArray(behaviourComponents)) behaviourComponents = [behaviourComponents]
    if (!Array.isArray(levels)) levels = [levels]

    return this.apolloMobileClient
    .query({
      query: gql`query getPeriodsForBehaviourComponents(
          $patientId : String!,
          $behaviourComponents: [String!]!
      ){
            myBehaviourComponentLogs(
                functions : {function : patientId, parameters : [$patientId]}
                conditions : {field : key, operator : in, parameters: $behaviourComponents}
                orders : [{field : createdAt, direction : asc}]
            ){
                component{
                    key
                }
                createdAt
                logId
                oldLevel
                newLevel
            }
        }
      `,
      variables: { patientId, behaviourComponents },
    }).then(result => {
      const ret = {}
      let dates = {}

      behaviourComponents.forEach(behaviourComponent => {
        ret[behaviourComponent] = []
        dates[behaviourComponent] = {start : null, end : null}
      })

      // Push finished periods
      result.data.myBehaviourComponentLogs.forEach(log => {
          const behaviourComponent = log.component.key

          if (dates[behaviourComponent].start === null){
            if (levels.includes(log.newLevel) && !levels.includes(log.oldLevel)){
              dates[behaviourComponent].level = log.newLevel;
              dates[behaviourComponent].start = asMoment ? moment(log.createdAt) : log.createdAt
            }
          } else if(dates[behaviourComponent].start !== null && dates[behaviourComponent].end === null){
            if (levels.includes(log.oldLevel)){
              dates[behaviourComponent].level = log.oldLevel;
              dates[behaviourComponent].end = asMoment ? moment(log.createdAt) : log.createdAt
              ret[behaviourComponent].push(dates[behaviourComponent])
              dates[behaviourComponent] = {start : null, end : null}
            }
            if (levels.includes(log.newLevel)){
              dates[behaviourComponent].level = log.newLevel;
              dates[behaviourComponent].start = asMoment ? moment(log.createdAt) : log.createdAt
              dates[behaviourComponent].end = null
            }
          }
      })

      // Push unfinished periods
      for (let behaviourComponent in dates){
        if (dates[behaviourComponent].start !== null && dates[behaviourComponent].end === null) {
          ret[behaviourComponent].push(dates[behaviourComponent])
        }
      }

      return ret
    })
  }

  getBehaviourComponentName(behaviourComponentKey){
    let trans = {key : 'behaviourComponentJourney.' + behaviourComponentKey};

    if (behaviourComponentKey === 'MEDICATIONS'){
      trans.key = 'screens.medicationAdherenceScreen';
      trans.catalog = 'app_mobile'
    } else if (behaviourComponentKey === 'HEALTHYNUTRITION'){
      trans.key = 'screens.healthyNutritionSettingsScreen';
      trans.catalog = 'app_mobile'
    }

    return ucfirst(this.translationManager.translate(trans))
  }
}